/**
 * BookingWidget - Orchestrator Component
 * Composes BookingForm, BookingSummary, and BookingActions
 * Contains ONLY state management and API orchestration
 *
 * i18n (Phase 5):
 *   - Forwards { t } from useTranslation() down to all child components.
 *   - Uses payment.* namespace for the user-visible alert() error strings.
 *   - RTL / Arabic locale support was removed in Phase 1; isRTL prop is
 *     retained as a hardcoded `false` for now to keep the child component
 *     prop signatures stable across future cleanup. Removing it entirely is
 *     a separate refactor (out of scope for the i18n rollout).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS, ru, es, de } from 'date-fns/locale';

// Modular components
import { BookingForm, BookingSummary, BookingActions } from './BookingWidget/index';

// Services layer
import { bookingService, paymentService } from '../services';

const dateLocales = { fr, en: enUS, ru, es, de };

export const BookingWidget = ({ compact = false }) => {
  const { t, i18n } = useTranslation();
  // RTL was removed in Phase 1. Kept as a stable false-valued prop so child
  // components don't have to lose their isRTL parameter mid-rollout.
  const isRTL = false;
  const currentLocale = dateLocales[i18n.language] || fr;

  // =========================================================================
  // STATE
  // =========================================================================
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [liveEstimate, setLiveEstimate] = useState(null);
  const [rideOptions, setRideOptions] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  // Driver availability is now derived from rideOptions response (pickup time estimates)

  const [formData, setFormData] = useState({
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    destination_address: '',
    destination_lat: null,
    destination_lng: null,
    date: null,
    time: '',
    passengers: '1',
    phone: '',
    email: '',
    customer_name: '',
    notes: '',
    service_type: null
  });

  // =========================================================================
  // EFFECTS - Live estimate on address change
  // =========================================================================
  useEffect(() => {
    const fetchLiveEstimate = async () => {
      if (formData.pickup_address?.length > 5 && formData.destination_address?.length > 5) {
        try {
          // Fetch full ride-options so the user sees all tier prices inline in
          // Step 1 (eco/business/van; G3.E removed PRIORITY). The compact "live estimate"
          // preview is derived from the cheapest option (eco when present) so
          // existing UI keeps working unchanged. When the user clicks Continue,
          // Step 2 reuses this same `rideOptions` state — no second API call.
          const result = await bookingService.getRideOptions({
            pickupAddress: formData.pickup_address,
            destinationAddress: formData.destination_address,
            pickupLat: formData.pickup_lat,
            pickupLng: formData.pickup_lng,
            destinationLat: formData.destination_lat,
            destinationLng: formData.destination_lng,
            passengers: parseInt(formData.passengers) || 1,
          });
          setRideOptions(result);
          const cheapest = result.options?.find(o => o.service_type === 'eco') || result.options?.[0];
          if (cheapest && result.route_info) {
            setLiveEstimate({
              estimated_price: cheapest.estimated_price,
              distance_km: result.route_info.distance_km,
              duration_minutes: result.route_info.duration_minutes,
              is_fixed_fare: result.is_fixed_fare,
              surge_active: result.surge_active,
            });
          }
        } catch (error) {
          // Fall back to single estimate so the preview still works if
          // ride-options ever fails (unlikely, but cheap insurance).
          console.error('Live ride-options error, falling back to single estimate:', error);
          try {
            const fallback = await bookingService.getEstimate({
              pickupAddress: formData.pickup_address,
              destinationAddress: formData.destination_address,
              pickupLat: formData.pickup_lat,
              pickupLng: formData.pickup_lng,
              destinationLat: formData.destination_lat,
              destinationLng: formData.destination_lng,
            });
            setLiveEstimate(fallback);
          } catch (fallbackError) {
            console.error('Live estimate fallback also failed:', fallbackError);
          }
        }
      } else {
        setLiveEstimate(null);
        setRideOptions(null);
        setSelectedOption(null);
      }
    };

    const debounce = setTimeout(fetchLiveEstimate, 800);
    return () => clearTimeout(debounce);
  }, [formData.pickup_address, formData.destination_address, formData.pickup_lat, formData.pickup_lng, formData.destination_lat, formData.destination_lng, formData.passengers]);

  // =========================================================================
  // HANDLERS
  // =========================================================================
  const handleContinueToStep2 = useCallback(async () => {
    if (!formData.pickup_address || !formData.destination_address || !formData.date || !formData.time) {
      return;
    }

    // Step 1 now fetches ride-options live (debounce effect above) so by the
    // time the user clicks Continue, `rideOptions` is usually already
    // populated and a tier may already be selected. Skip the redundant fetch.
    if (rideOptions?.options?.length) {
      // Sync `estimate` from the chosen tier (or the default cheapest) so
      // BookingSummary's price line stays accurate on Step 2.
      const chosen = selectedOption
        || rideOptions.options.find(o => o.service_type === 'eco')
        || rideOptions.options[0];
      if (chosen) {
        setEstimate({
          estimated_price: chosen.estimated_price,
          distance_km: rideOptions.route_info?.distance_km,
          duration_minutes: rideOptions.route_info?.duration_minutes,
          is_fixed_fare: rideOptions.is_fixed_fare,
        });
      }
      setStep(2);
      return;
    }

    // Fallback path — debounce hasn't fired yet (e.g. user clicked Continue
    // immediately after picking addresses, before the 800ms timer). Same
    // logic as before: fetch options, advance, fall back to single estimate
    // on failure.
    setEstimating(true);
    setLoadingOptions(true);
    setStep(2);

    try {
      const result = await bookingService.getRideOptions({
        pickupAddress: formData.pickup_address,
        destinationAddress: formData.destination_address,
        pickupLat: formData.pickup_lat,
        pickupLng: formData.pickup_lng,
        destinationLat: formData.destination_lat,
        destinationLng: formData.destination_lng,
        passengers: parseInt(formData.passengers)
      });

      setRideOptions(result);

      const ecoOption = result.options?.find(o => o.service_type === 'eco');
      if (ecoOption) {
        setEstimate({
          estimated_price: ecoOption.estimated_price,
          distance_km: result.route_info.distance_km,
          duration_minutes: result.route_info.duration_minutes,
          is_fixed_fare: result.is_fixed_fare
        });
      }
    } catch (error) {
      console.error('Ride options error:', error);
      try {
        const fallback = await bookingService.getEstimate({
          pickupAddress: formData.pickup_address,
          destinationAddress: formData.destination_address,
          pickupLat: formData.pickup_lat,
          pickupLng: formData.pickup_lng,
          destinationLat: formData.destination_lat,
          destinationLng: formData.destination_lng,
        });
        setEstimate(fallback);
      } catch (fallbackError) {
        console.error('Estimation error:', fallbackError);
      }
    } finally {
      setEstimating(false);
      setLoadingOptions(false);
    }
  }, [formData, rideOptions, selectedOption]);

  const handleSelectOption = useCallback((option) => {
    setSelectedOption(option);
    setFormData(prev => ({
      ...prev,
      service_type: option.service_type
    }));
    setEstimate(prev => ({
      ...prev,
      estimated_price: option.estimated_price
    }));
  }, []);

  const handlePayment = useCallback(async (e) => {
    e?.preventDefault();
    if (!formData.phone || !formData.email) return;

    setLoading(true);
    try {
      // Detect corporate employee
      const employeeToken = localStorage.getItem('employeeToken');
      const employeeInfo = employeeToken ? JSON.parse(localStorage.getItem('employeeInfo') || '{}') : null;
      const corporateAccountId = employeeInfo?.corporate_account_id || null;

      const result = await paymentService.createStripeSession({
        pickupAddress: formData.pickup_address,
        destinationAddress: formData.destination_address,
        pickupLat: formData.pickup_lat,
        pickupLng: formData.pickup_lng,
        destinationLat: formData.destination_lat,
        destinationLng: formData.destination_lng,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        passengers: parseInt(formData.passengers),
        phone: formData.phone,
        email: formData.email,
        notes: formData.notes,
        customerName: formData.customer_name,
        acceptedTerms: acceptedTerms,
        serviceType: selectedOption?.service_type || 'eco',
        isEnterprise: !!corporateAccountId,
        enterpriseId: corporateAccountId,
      });

      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(t('payment.paymentError'));
    } finally {
      setLoading(false);
    }
  }, [formData, acceptedTerms, selectedOption, t]);

  const handleBack = useCallback(() => {
    setStep(1);
  }, []);

  // =========================================================================
  // RENDER - Thin orchestrator only
  // =========================================================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl ${compact ? 'p-4' : 'p-6 md:p-8'}`}
      data-testid="booking-widget"
    >
      <form onSubmit={handlePayment}>
        {step === 1 && (
          <BookingForm
            formData={formData}
            setFormData={setFormData}
            liveEstimate={liveEstimate}
            rideOptions={rideOptions}
            selectedOption={selectedOption}
            onSelectOption={handleSelectOption}
            onContinue={handleContinueToStep2}
            isRTL={isRTL}
            currentLocale={currentLocale}
            t={t}
          />
        )}

        {step === 2 && (
          <div className="space-y-4">
            <BookingSummary
              loadingOptions={loadingOptions}
              rideOptions={rideOptions}
              selectedOption={selectedOption}
              estimate={estimate}
              estimating={estimating}
              onSelectOption={handleSelectOption}
              t={t}
            />

            <BookingActions
              formData={formData}
              setFormData={setFormData}
              acceptedTerms={acceptedTerms}
              setAcceptedTerms={setAcceptedTerms}
              termsError={termsError}
              setTermsError={setTermsError}
              loading={loading}
              rideOptions={rideOptions}
              selectedOption={selectedOption}
              estimate={estimate}
              onBack={handleBack}
              onSubmit={handlePayment}
              isRTL={isRTL}
              t={t}
            />
          </div>
        )}
      </form>
    </motion.div>
  );
};

export default BookingWidget;
